import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppState, Platform } from 'react-native';
import type { ChannelPostCommentDto, ChannelPostCommentWsEvent, MessageDto } from '../api/types';
import { normalizeMessage } from './normalizeMessage';
import { isCredentialRejectedHttpStatus } from '../api/authHttp';
import {
  loadStoredAccess,
  probeAuth,
  trySilentRefresh,
  type CallSessionSummary,
  type TranscriptState,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { API_BASE, wsAuthHeaders, wsUrlCandidates } from '../config';
import { resolveWsAccessToken } from './wsAccessToken';
type NativeWebSocketCtor = new (
  url: string,
  protocols?: string | string[],
  options?: { headers?: Record<string, string> },
) => WebSocket;

import { playIncomingMessageSound } from '../util/playIncomingMessageSound';
import { decodePayload, previewTextFromCiphertext } from '../util/chatPayload';
import { tr } from '../i18n/runtime';
import {
  markIncomingCallHandledFromWebSocket,
  tryClaimMessageNotify,
} from '../notifications/pushDedupe';
import { notifyIncomingMessage } from './notifyIncomingMessage';
import { localNotificationBody } from '../notifications/notificationText';
import { incomingMessageEffects } from './incomingMessageEffects';
import { voiceCallLog, voiceCallWarn } from '../calls/voiceCallDebug';
import { isChatMutedSync } from '../screens/chatNotificationPrefsStorage';
import { callerAvatarFromWsSignal, presentIncomingCallUI } from '../calls/incomingCallUiBridge';
import { cancelRingingIncomingCall } from '../calls/cancelRingingIncomingCall';
import { decideIncomingRingCancel } from '../calls/incomingRingCancelRules';
import { getRingingIncomingCall } from '../calls/ringingIncomingCallRegistry';
import { extractCallInviteIdFromSignal } from '../calls/callLegSignal';
import { getActiveCall } from '../calls/activeCallRegistry';
import { decideIncomingInviteWhileBusy } from '../calls/callBusyRules';
import { hasLocalOutgoingCallInvite } from '../calls/localOutgoingCallInviteGate';
import { nativeInvitePendingUuid, parseWsInvitePayload } from './inviteSignalPayload';
import { logCallLifecycleEnd, logCallSignalLine } from '../calls/callLifecycleDebug';
import { reportError } from '../observability/errorReporter';
import { shouldAbortWsConnect } from './wsConnectGuards';
import { wsConnectGenerationProbe } from './wsConnectGenerationProbe';
import { appendSignalBacklog } from './signalBacklog';

/**
 * После скольких неудачных попыток подряд обрыв связи перестаёт быть «плохой сетью» и становится
 * поломкой, о которой стоит сообщить. Пятая попытка — это примерно полминуты без сокета.
 */
const WS_RECONNECT_REPORT_AFTER = 5;

export type RouteSnapshot = { name: string; chatId?: string };
type PresenceEvent = {
  userId?: string;
  chatId?: string;
  online: boolean;
};
type MessageReadEvent = {
  messageIds?: string[];
  readUpTo?: string;
  readByUserId?: string;
};

type GlobalRealtimeValue = {
  wsConnected: boolean;
  subscribeMessages: (chatId: string, fn: (m: MessageDto) => void) => () => void;
  subscribeMessageDeletes: (chatId: string, fn: (messageId: string) => void) => () => void;
  subscribeMessageReads: (chatId: string, fn: (event: MessageReadEvent) => void) => () => void;
  subscribeIncomingMessages: (fn: (chatId: string) => void) => () => void;
  subscribePresence: (fn: (event: PresenceEvent) => void) => () => void;
  subscribeSignals: (chatId: string, fn: (rawJson: string) => void) => () => void;
  subscribeChannelPostComments: (
    chatId: string,
    postId: string,
    fn: (ev: ChannelPostCommentWsEvent) => void,
  ) => () => void;
  subscribeChatDeleted: (fn: (chatId: string) => void) => () => void;
  subscribeDirectMessagesPurged: (fn: (chatId: string) => void) => () => void;
  /** `null` — звонок в чате закончился (или его не было). */
  subscribeCallSession: (
    fn: (chatId: string, session: CallSessionSummary | null) => void,
  ) => () => void;
  subscribeChatListChanged: (fn: () => void) => () => void;
  subscribeContactsChanged: (fn: () => void) => () => void;
  /** Готовая расшифровка голосового в этом чате (эпик 21, стори 21.5). */
  subscribeVoiceTranscripts: (
    chatId: string,
    fn: (state: TranscriptState) => void,
  ) => () => void;
  sendSignal: (chatId: string, payload: Record<string, unknown>) => void;
  refreshSubscribeAll: () => void;
};

const Ctx = createContext<GlobalRealtimeValue | null>(null);

/** Outbound SIGNAL while socket is down; ICE trickle + glare retries need headroom. */
const MAX_QUEUED_SIGNALS = 128;
/**
 * Пока ни один экран не вызвал subscribeSignals(chatId), offer/ICE не теряем.
 * Trickle ICE может дать сотни кадров — малый буфер ломал соединение после навигации.
 */
const MAX_SIGNAL_BACKLOG_PER_CHAT = 384;
const AUTH_CLOSE_CODES = new Set([1008, 4401, 4403]);

function looksLikeAuthClose(code: number, reason: string): boolean {
  if (AUTH_CLOSE_CODES.has(code)) return true;
  const r = (reason || '').toLowerCase();
  return (
    r.includes('unauthor') ||
    r.includes('forbidden') ||
    r.includes('jwt') ||
    (r.includes('token') && (r.includes('invalid') || r.includes('expired') || r.includes('missing')))
  );
}

function normUserId(id: string): string {
  return id.replace(/-/g, '').toLowerCase();
}

function isCallEventMessage(ciphertext: string): boolean {
  try {
    const p = decodePayload(ciphertext);
    return p.type === 'text' && p.text.trim().startsWith('📞');
  } catch {
    return false;
  }
}

/** Единый ключ чата для Map подписок (WS и навигация могут отличаться регистром/пробелами). */
function canonChatId(id: string): string {
  return id.trim().replace(/-/g, '').toLowerCase();
}

async function fetchWebWsTicket(
  accessToken: string,
  onAccessRefreshed?: (newAccess: string) => void,
): Promise<string> {
  const post = (token: string) =>
    fetch(`${API_BASE}/api/auth/ws-ticket`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  let res = await post(accessToken);
  if (isCredentialRejectedHttpStatus(res.status) && onAccessRefreshed) {
    const r = await trySilentRefresh();
    if (r.status === 'refreshed') {
      onAccessRefreshed(r.accessToken);
      res = await post(r.accessToken);
    }
  }
  if (!res.ok) {
    throw new Error('Failed to create websocket ticket');
  }
  const data = (await res.json()) as { wsTicket?: unknown };
  if (typeof data?.wsTicket !== 'string' || data.wsTicket.trim().length === 0) {
    throw new Error('Invalid websocket ticket response');
  }
  return data.wsTicket;
}


export function GlobalRealtimeProvider({
  children,
  routeRef,
}: {
  children: React.ReactNode;
  routeRef: React.MutableRefObject<RouteSnapshot>;
}) {
  const { accessToken, user, invalidateSession, applyAccessToken } = useAuth();
  const userRef = useRef(user);
  // Кадры WS читают пользователя из ref; обновляем его в layout-эффекте, до обычных
  // эффектов (в том числе до подключения сокета), а не записью во время рендера.
  useLayoutEffect(() => {
    userRef.current = user;
  }, [user]);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  /** Monotonic SIGNAL seq per chat (canonical id) for reconnect / duplicate detection. */
  const outboundSignalSeqByChatKeyRef = useRef<Map<string, number>>(new Map());
  const signalQueueRef = useRef<string[]>([]);
  const lastProbeAtRef = useRef(0);
  const msgListeners = useRef(new Map<string, Set<(m: MessageDto) => void>>());
  const messageDeleteListeners = useRef(new Map<string, Set<(messageId: string) => void>>());
  const incomingMessageListeners = useRef(new Set<(chatId: string) => void>());
  const messageReadListeners = useRef(new Map<string, Set<(event: MessageReadEvent) => void>>());
  const presenceListeners = useRef(new Set<(event: PresenceEvent) => void>());
  const sigListeners = useRef(new Map<string, Set<(raw: string) => void>>());
  const signalBacklogRef = useRef(new Map<string, string[]>());
  const chatDeletedListeners = useRef(new Set<(chatId: string) => void>());
  /**
   * Готовые расшифровки голосовых (эпик 21, стори 21.5). По чату, а не глобально: кадр нужен
   * только тому экрану, который этот чат сейчас показывает.
   */
  const transcriptListeners = useRef(
    new Map<string, Set<(state: TranscriptState) => void>>(),
  );
  const directMessagesPurgedListeners = useRef(new Set<(chatId: string) => void>());
  /**
   * Состояние звонка в чате. Приезжает по тому же WS, что и всё остальное, и нужно даже тем, кто
   * в звонке не участвует: без него в чате нигде не видно, что разговор идёт, и присоединиться
   * можно было только успев принять первый вызов (стори 7.1).
   */
  const callSessionListeners = useRef(
    new Set<(chatId: string, session: CallSessionSummary | null) => void>(),
  );
  const chatListChangedListeners = useRef(new Set<() => void>());
  const contactsChangedListeners = useRef(new Set<() => void>());
  const channelPostCommentListeners = useRef(
    new Map<string, Set<(ev: ChannelPostCommentWsEvent) => void>>(),
  );

  const subscribeMessages = useCallback((chatId: string, fn: (m: MessageDto) => void) => {
    const key = canonChatId(chatId);
    let set = msgListeners.current.get(key);
    if (!set) {
      set = new Set();
      msgListeners.current.set(key, set);
    }
    set.add(fn);
    return () => {
      set!.delete(fn);
      if (set!.size === 0) msgListeners.current.delete(key);
    };
  }, []);

  const subscribeMessageDeletes = useCallback((chatId: string, fn: (messageId: string) => void) => {
    const key = canonChatId(chatId);
    let set = messageDeleteListeners.current.get(key);
    if (!set) {
      set = new Set();
      messageDeleteListeners.current.set(key, set);
    }
    set.add(fn);
    return () => {
      set!.delete(fn);
      if (set!.size === 0) messageDeleteListeners.current.delete(key);
    };
  }, []);

  const dispatchSignalToChatListeners = useCallback((chatIdRaw: string, rawStr: string) => {
    const chatKey = canonChatId(chatIdRaw);
    const set = sigListeners.current.get(chatKey);
    if (set && set.size > 0) {
      set.forEach((listener) => listener(rawStr));
      return;
    }
    const buf = appendSignalBacklog(
      signalBacklogRef.current.get(chatKey) ?? [],
      rawStr,
      MAX_SIGNAL_BACKLOG_PER_CHAT,
    );
    signalBacklogRef.current.set(chatKey, buf);
    voiceCallLog('SIGNAL buffered until subscribeSignals', { chatKey, backlogLen: buf.length });
  }, []);

  const subscribeSignals = useCallback(
    (chatId: string, fn: (raw: string) => void) => {
      const key = canonChatId(chatId);
      let set = sigListeners.current.get(key);
      if (!set) {
        set = new Set();
        sigListeners.current.set(key, set);
      }
      set.add(fn);
      const backlog = signalBacklogRef.current.get(key);
      if (backlog?.length) {
        voiceCallLog('Flushing SIGNAL backlog to new subscriber', { key, count: backlog.length });
        signalBacklogRef.current.delete(key);
        for (const raw of backlog) {
          set.forEach((listener) => listener(raw));
        }
      }
      return () => {
        set!.delete(fn);
        if (set!.size === 0) sigListeners.current.delete(key);
      };
    },
    [],
  );

  const subscribeIncomingMessages = useCallback((fn: (chatId: string) => void) => {
    incomingMessageListeners.current.add(fn);
    return () => {
      incomingMessageListeners.current.delete(fn);
    };
  }, []);

  const subscribeMessageReads = useCallback((chatId: string, fn: (event: MessageReadEvent) => void) => {
    const key = canonChatId(chatId);
    let set = messageReadListeners.current.get(key);
    if (!set) {
      set = new Set();
      messageReadListeners.current.set(key, set);
    }
    set.add(fn);
    return () => {
      set!.delete(fn);
      if (set!.size === 0) messageReadListeners.current.delete(key);
    };
  }, []);

  const subscribePresence = useCallback((fn: (event: PresenceEvent) => void) => {
    presenceListeners.current.add(fn);
    return () => {
      presenceListeners.current.delete(fn);
    };
  }, []);

  const subscribeChatDeleted = useCallback((fn: (chatId: string) => void) => {
    chatDeletedListeners.current.add(fn);
    return () => {
      chatDeletedListeners.current.delete(fn);
    };
  }, []);

  const subscribeDirectMessagesPurged = useCallback((fn: (chatId: string) => void) => {
    directMessagesPurgedListeners.current.add(fn);
    return () => {
      directMessagesPurgedListeners.current.delete(fn);
    };
  }, []);

  const subscribeCallSession = useCallback(
    (fn: (chatId: string, session: CallSessionSummary | null) => void) => {
      callSessionListeners.current.add(fn);
      return () => {
        callSessionListeners.current.delete(fn);
      };
    },
    [],
  );

  const subscribeChatListChanged = useCallback((fn: () => void) => {
    chatListChangedListeners.current.add(fn);
    return () => {
      chatListChangedListeners.current.delete(fn);
    };
  }, []);

  const subscribeContactsChanged = useCallback((fn: () => void) => {
    contactsChangedListeners.current.add(fn);
    return () => {
      contactsChangedListeners.current.delete(fn);
    };
  }, []);

  /**
   * Готовая расшифровка голосового в этом чате (эпик 21, стори 21.5).
   *
   * <p>Кадр приходит всем участникам, включая автора записи: текст один на всех.
   */
  const subscribeVoiceTranscripts = useCallback(
    (chatId: string, fn: (state: TranscriptState) => void) => {
      const key = canonChatId(chatId);
      let set = transcriptListeners.current.get(key);
      if (!set) {
        set = new Set();
        transcriptListeners.current.set(key, set);
      }
      set.add(fn);
      return () => {
        set!.delete(fn);
        if (set!.size === 0) transcriptListeners.current.delete(key);
      };
    },
    [],
  );

  const subscribeChannelPostComments = useCallback(
    (chatId: string, postId: string, fn: (ev: ChannelPostCommentWsEvent) => void) => {
      const k = `${canonChatId(chatId)}::${canonChatId(postId)}`;
      let set = channelPostCommentListeners.current.get(k);
      if (!set) {
        set = new Set();
        channelPostCommentListeners.current.set(k, set);
      }
      set.add(fn);
      return () => {
        set!.delete(fn);
        if (set!.size === 0) channelPostCommentListeners.current.delete(k);
      };
    },
    [],
  );

  const sendSignal = useCallback((chatId: string, payload: Record<string, unknown>) => {
    const ws = wsRef.current;
    const signalType = String(payload.signalType ?? '?');
    const chatKey = canonChatId(chatId);
    const nextSeq = (outboundSignalSeqByChatKeyRef.current.get(chatKey) ?? 0) + 1;
    outboundSignalSeqByChatKeyRef.current.set(chatKey, nextSeq);
    const json = JSON.stringify({ type: 'SIGNAL', chatId, ...payload, signalSeq: nextSeq });
    const callInviteRaw = payload.callInviteId;
    const callId =
      typeof callInviteRaw === 'string'
        ? callInviteRaw
        : (() => {
            try {
              const p = typeof payload.payload === 'string' ? JSON.parse(payload.payload) : null;
              const id =
                p && typeof p === 'object' && p !== null && 'callInviteId' in p
                  ? (p as { callInviteId?: unknown }).callInviteId
                  : undefined;
              return typeof id === 'string' ? id : null;
            } catch {
              return null;
            }
          })();
    logCallSignalLine({
      type: signalType,
      direction: 'outgoing',
      callId,
      chatId,
    });
    if (ws?.readyState === WebSocket.OPEN) {
      voiceCallLog('WS send SIGNAL', { chatId, signalType });
      ws.send(json);
    } else if (signalQueueRef.current.length < MAX_QUEUED_SIGNALS) {
      voiceCallWarn('WS not OPEN, SIGNAL queued', {
        chatId,
        signalType,
        readyState: ws?.readyState,
        queueLen: signalQueueRef.current.length,
      });
      signalQueueRef.current.push(json);
    } else {
      voiceCallWarn('SIGNAL dropped (queue full)', { chatId, signalType, max: MAX_QUEUED_SIGNALS });
    }
  }, []);

  const refreshSubscribeAll = useCallback(() => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'SUBSCRIBE_ALL' }));
    }
  }, []);

  useEffect(() => {
    if (!accessToken) {
      logCallLifecycleEnd('ws_close_access_token_cleared', {
        extra: { hadSocket: wsRef.current != null },
      });
      signalQueueRef.current = [];
      signalBacklogRef.current.clear();
      outboundSignalSeqByChatKeyRef.current.clear();
      wsRef.current?.close();
      wsRef.current = null;
      return;
    }
    let isActive = true;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempt = 0;
    let wsCandidateIndex = 0;
    let wsPingTimer: ReturnType<typeof setInterval> | null = null;
    /** Bumps on each connect() start and on effect cleanup — invalidates in-flight work after await. */
    let connectGeneration = 0;

    const bumpConnectGeneration = (reason: string) => {
      connectGeneration += 1;
      wsConnectGenerationProbe.current = connectGeneration;
      voiceCallLog('WS connectGeneration bump', { reason, nextGen: connectGeneration });
    };

    const scheduleReconnect = () => {
      if (!isActive || reconnectTimer) {
        return;
      }
      const delayMs = Math.min(1000 * 2 ** reconnectAttempt, 15000);
      reconnectAttempt += 1;
      voiceCallWarn('WS reconnect scheduled', { attempt: reconnectAttempt, delayMs });
      // OBS-2. Одна неудачная попытка — это плохая связь, и молчать о ней правильно. Но пятая
      // подряд означает, что сокет не поднимается вообще: человек в этот момент видит чат, в
      // который «не приходят сообщения», и не понимает почему. Ровно один отчёт на серию —
      // дальше повторы отсекает дедупликация в самом приёмнике.
      if (reconnectAttempt === WS_RECONNECT_REPORT_AFTER) {
        void reportError('realtime', new Error('websocket keeps failing to connect'), {
          level: 'warning',
          context: { попыток: reconnectAttempt, следующаяЧерезМс: delayMs },
        });
      }
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, delayMs);
    };

    const connect = async () => {
      if (!isActive) {
        return;
      }
      const myGeneration = ++connectGeneration;
      wsConnectGenerationProbe.current = connectGeneration;
      if (wsPingTimer) {
        clearInterval(wsPingTimer);
        wsPingTimer = null;
      }
      // Свежий токен на каждую попытку — см. wsAccessToken.ts: иначе после истечения
      // access-токена сокет переподключается по кругу со старым.
      const token = await resolveWsAccessToken(loadStoredAccess, accessToken);
      if (shouldAbortWsConnect(isActive, myGeneration, connectGeneration)) {
        logCallLifecycleEnd('ws_connect_aborted_after_token_read', {
          extra: { myGeneration, connectGeneration },
        });
        return;
      }
      let wsTicket: string | undefined;
      if (Platform.OS === 'web') {
        try {
          wsTicket = await fetchWebWsTicket(token, applyAccessToken);
        } catch (e) {
          voiceCallWarn('WS ticket fetch failed', e);
          scheduleReconnect();
          return;
        }
        if (shouldAbortWsConnect(isActive, myGeneration, connectGeneration)) {
          logCallLifecycleEnd('ws_connect_aborted_after_ticket', {
            extra: { myGeneration, connectGeneration },
          });
          return;
        }
      }
      if (shouldAbortWsConnect(isActive, myGeneration, connectGeneration)) {
        logCallLifecycleEnd('ws_connect_aborted_pre_socket', {
          extra: { myGeneration, connectGeneration },
        });
        return;
      }
      const candidates = wsUrlCandidates(wsTicket);
      if (wsCandidateIndex >= candidates.length) {
        wsCandidateIndex = 0;
      }
      const selectedUrl = candidates[wsCandidateIndex] ?? candidates[0];
      const ws =
        Platform.OS === 'web'
          ? new WebSocket(selectedUrl)
          : new (WebSocket as unknown as NativeWebSocketCtor)(selectedUrl, undefined, {
              headers: wsAuthHeaders(token),
            });
      if (shouldAbortWsConnect(isActive, myGeneration, connectGeneration)) {
        logCallLifecycleEnd('ws_connect_aborted_after_socket_ctor', {
          extra: { myGeneration, connectGeneration, isActive },
        });
        try {
          ws.close();
        } catch {
          /* noop */
        }
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        reconnectAttempt = 0;
        wsCandidateIndex = 0;
        setWsConnected(true);
        voiceCallLog('WS open', {
          url: typeof ws.url === 'string' ? ws.url.replace(/\?.*$/, '') : '?',
        });
        // Достаточно одного SUBSCRIBE_ALL на коннект: сервер подписывает сессию на все чаты,
        // а новые/удалённые чаты прилетают как CHAT_LIST_CHANGED, после чего вызываем
        // refreshSubscribeAll() локально. Раньше клиент слал SUBSCRIBE_ALL каждые 20 секунд —
        // на 1000 пользователях это давало 3000 SELECT/мин на сервере без какой-либо пользы.
        ws.send(JSON.stringify({ type: 'SUBSCRIBE_ALL' }));
        // Уведомляем подписчиков о переподключении, чтобы экраны обновили свои данные
        // (список чатов, сообщения) и не пропустили события, пришедшие пока WS был отключён.
        chatListChangedListeners.current.forEach((fn) => fn());
        const q = signalQueueRef.current.length;
        if (q) voiceCallLog('WS flushing queued signals', { count: q });
        while (signalQueueRef.current.length > 0 && ws.readyState === WebSocket.OPEN) {
          ws.send(signalQueueRef.current.shift()!);
        }
        void import('./notifyIncomingMessage').then((m) => m.ensureNotifyPermission());
        wsPingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify({ type: 'PING' }));
            } catch (pingErr) {
              voiceCallWarn('WS PING send failed', pingErr);
            }
          }
        }, 15000);
      };

      ws.onerror = (e) => {
        voiceCallWarn('WS error', e);
      };

      ws.onclose = (ev) => {
        logCallLifecycleEnd('ws_onclose', {
          extra: {
            code: ev.code,
            reason: ev.reason,
            wasClean: ev.wasClean,
            sameRefAsActive: wsRef.current === ws,
          },
        });
        setWsConnected(false);
        if (wsPingTimer) {
          clearInterval(wsPingTimer);
          wsPingTimer = null;
        }
        voiceCallLog('WS close', { code: ev.code, reason: ev.reason, wasClean: ev.wasClean });
        // Do NOT clear signalBacklogRef here: signals may be buffered until CallScreen
        // subscribes; clearing on every reconnect dropped invite/offer/ICE (audit).
        if (wsRef.current === ws) {
          wsRef.current = null;
        }
        const reasonText = String(ev.reason || '');
        if (
          ev.code !== 1000 &&
          /Expected HTTP 101 response but was ['"]?200/.test(reasonText) &&
          wsCandidateIndex + 1 < wsUrlCandidates(wsTicket).length
        ) {
          wsCandidateIndex += 1;
          voiceCallWarn('WS endpoint failed handshake, trying next candidate', {
            nextCandidateIndex: wsCandidateIndex,
          });
        }
        if (looksLikeAuthClose(ev.code, ev.reason)) {
          voiceCallWarn('WS auth close -> silent refresh', { code: ev.code, reason: ev.reason });
          signalQueueRef.current = [];
          signalBacklogRef.current.clear();
          void (async () => {
            const r = await trySilentRefresh();
            if (r.status === 'refreshed') {
              applyAccessToken(r.accessToken);
              return;
            }
            if (r.status === 'invalid_refresh') {
              invalidateSession();
              return;
            }
            scheduleReconnect();
          })();
          return;
        }
        // If WS drops (server restart), do a lightweight auth probe sometimes.
        // This ensures we auto-logout even if the user doesn't trigger HTTP calls.
        const now = Date.now();
        const shouldProbe = ev.code !== 1000 && now - lastProbeAtRef.current > 15000;
        if (shouldProbe) {
          lastProbeAtRef.current = now;
          if (Platform.OS === 'web') {
            void probeAuth().then((ok) => {
              if (!ok && isActive) {
                voiceCallWarn('WS auth probe failed -> logout', { platform: 'web' });
                signalQueueRef.current = [];
                signalBacklogRef.current.clear();
                invalidateSession();
                return;
              }
              if (isActive) {
                scheduleReconnect();
              }
            });
            return;
          }
          void probeAuth();
        }
        if (isActive) {
          scheduleReconnect();
        }
      };

      ws.onmessage = (ev) => {
        try {
          const rawStr = String(ev.data);
          const data = JSON.parse(rawStr) as {
            type?: string;
            chatId?: string;
            message?: unknown;
            signalType?: string;
            fromUserId?: string;
            userId?: string;
            peerId?: string;
            targetUserId?: string;
            online?: boolean;
            isOnline?: boolean;
            payload?: string;
            update?: boolean;
          };
          if (data.type === 'PONG') {
            return;
          }
          if (data.type === 'MESSAGE' && data.chatId && data.message) {
            try {
              const m = normalizeMessage(data.message);
              const chatIdRaw = String(data.chatId);
              const chatKey = canonChatId(chatIdRaw);
              // Пузырь обновляем всегда — и на новое сообщение, и на правку.
              msgListeners.current.get(chatKey)?.forEach((fn) => fn(m));

              const route = routeRef.current;
              const norm = (id: string) => id.replace(/-/g, '').toLowerCase();
              const uid = user?.userId != null ? norm(String(user.userId)) : '';
              const sid = norm(String(m.senderId));
              const effects = incomingMessageEffects({
                mine: uid !== '' && sid === uid,
                // Реакция, галочка в чеклисте, правка анонса статьи: сервер переиздаёт то же
                // сообщение, и `senderId` в кадре — автор исходного, а не тот, кто нажал.
                isUpdate: data.update === true,
                isCallEvent: () => isCallEventMessage(m.ciphertext),
                // Синхронная проверка mute из in-memory snapshot — никакого disk I/O в hot-path WS.
                muted: isChatMutedSync(chatIdRaw),
                viewingThisChat:
                  (route.name === 'Chat' || route.name === 'Call') &&
                  route.chatId != null &&
                  canonChatId(route.chatId) === chatKey,
                appActive: AppState.currentState === 'active',
              });
              if (effects.sound) {
                void playIncomingMessageSound();
              }
              if (effects.bumpUnread) {
                incomingMessageListeners.current.forEach((fn) => fn(chatIdRaw));
              }
              if (effects.banner && tryClaimMessageNotify(m.id)) {
                void notifyIncomingMessage({
                  chatId: chatIdRaw,
                  title: m.senderDisplayName?.trim() || tr('newMessage'),
                  body: localNotificationBody(
                    userRef.current?.showMessageText,
                    previewTextFromCiphertext(m.ciphertext),
                  ),
                  messageId: m.id,
                });
              }
            } catch (msgErr) {
              voiceCallWarn('MESSAGE payload invalid', msgErr);
            }
          }
          if (data.type === 'MESSAGE_READ' && data.chatId) {
            const chatIdRaw = String(data.chatId);
            const chatKey = canonChatId(chatIdRaw);
            const rawIds = (data as { messageIds?: unknown }).messageIds;
            const messageIds = Array.isArray(rawIds) ? rawIds.map(String) : [];
            const readUpToRaw = (data as { readUpTo?: unknown }).readUpTo;
            const readByUserIdRaw = (data as { readByUserId?: unknown }).readByUserId;
            if (messageIds.length > 0 || readUpToRaw != null) {
              messageReadListeners.current.get(chatKey)?.forEach((fn) =>
                fn({
                  messageIds,
                  readUpTo: readUpToRaw != null ? String(readUpToRaw) : undefined,
                  readByUserId: readByUserIdRaw != null ? String(readByUserIdRaw) : undefined,
                }),
              );
            }
          }
          if (data.type === 'MESSAGE_DELETED' && data.chatId) {
            const mid = (data as { messageId?: unknown }).messageId;
            if (mid != null && String(mid).length > 0) {
              const chatIdRaw = String(data.chatId);
              const chatKey = canonChatId(chatIdRaw);
              messageDeleteListeners.current.get(chatKey)?.forEach((fn) => fn(String(mid)));
            }
          }
          if (data.type === 'CHANNEL_POST_COMMENT' && data.chatId) {
            const ext = data as { postId?: unknown; action?: unknown; comment?: unknown };
            const chatIdRaw = String(data.chatId);
            const postIdRaw = ext.postId != null ? String(ext.postId) : '';
            if (postIdRaw && ext.comment && typeof ext.comment === 'object') {
              const k = `${canonChatId(chatIdRaw)}::${canonChatId(postIdRaw)}`;
              const ev: ChannelPostCommentWsEvent = {
                action: String(ext.action || ''),
                postId: postIdRaw,
                chatId: chatIdRaw,
                comment: ext.comment as ChannelPostCommentDto,
              };
              channelPostCommentListeners.current.get(k)?.forEach((fn) => fn(ev));
            }
          }
          /**
           * Расшифровка голосового решилась (эпик 21, стори 21.5) — «готово» или
           * «окончательно не вышло». Кадр несёт статус и превью, но не весь текст: полный
           * забирает тот, кто действительно развернул расшифровку.
           */
          if (data.type === 'ATTACHMENT_TRANSCRIPT' && data.chatId) {
            const ext = data as { transcript?: unknown };
            const raw = ext.transcript;
            if (raw && typeof raw === 'object') {
              const t = raw as Record<string, unknown>;
              const attachmentId = t.attachmentId != null ? String(t.attachmentId) : '';
              const status = t.status != null ? String(t.status) : '';
              if (attachmentId && status) {
                const state: TranscriptState = {
                  id: attachmentId,
                  status: status as TranscriptState['status'],
                  lang: t.lang != null ? String(t.lang) : null,
                  preview: t.preview != null ? String(t.preview) : null,
                };
                const key = canonChatId(String(data.chatId));
                transcriptListeners.current.get(key)?.forEach((fn) => fn(state));
              }
            }
          }
          if (data.type === 'CHAT_DELETED' && data.chatId) {
            const chatIdRaw = String(data.chatId);
            chatDeletedListeners.current.forEach((fn) => fn(chatIdRaw));
          }
          if (data.type === 'DIRECT_MESSAGES_PURGED' && data.chatId) {
            const chatIdRaw = String(data.chatId);
            directMessagesPurgedListeners.current.forEach((fn) => fn(chatIdRaw));
          }
          if (data.type === 'CALL_SESSION' && data.chatId) {
            const chatIdRaw = String(data.chatId);
            const raw = (data as { session?: CallSessionSummary }).session ?? null;
            // Кадр приходит и на завершение звонка: статус ENDED означает «плашку убрать».
            const session = raw && raw.status === 'ACTIVE' ? raw : null;
            callSessionListeners.current.forEach((fn) => fn(chatIdRaw, session));
          }
          if (data.type === 'CHAT_LIST_CHANGED') {
            // Сами подпишемся на свежий список чатов — без 20-секундного polling'а.
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'SUBSCRIBE_ALL' }));
            }
            chatListChangedListeners.current.forEach((fn) => fn());
          }
          if (data.type === 'CONTACTS_CHANGED') {
            contactsChangedListeners.current.forEach((fn) => fn());
          }
          if (
            (data.type === 'USER_ONLINE' || data.type === 'USER_OFFLINE' || data.type === 'PRESENCE') &&
            (data.chatId != null ||
              data.fromUserId != null ||
              data.userId != null ||
              data.peerId != null ||
              data.targetUserId != null)
          ) {
            const online =
              data.type === 'USER_ONLINE'
                ? true
                : data.type === 'USER_OFFLINE'
                  ? false
                  : Boolean(data.online ?? data.isOnline ?? true);
            const userIdRaw =
              data.fromUserId ?? data.userId ?? data.peerId ?? data.targetUserId;
            presenceListeners.current.forEach((fn) =>
              fn({
                userId: userIdRaw != null ? String(userIdRaw) : undefined,
                chatId: data.chatId != null ? String(data.chatId) : undefined,
                online,
              }),
            );
          }
          if (data.type === 'SIGNAL' && data.chatId && data.signalType === 'invite') {
            const chatIdRaw = String(data.chatId);
            const chatKey = canonChatId(chatIdRaw);
            voiceCallLog('WS SIGNAL in', {
              chatId: chatIdRaw,
              signalType: 'invite',
              fromUserId: data.fromUserId,
              rawLen: rawStr.length,
            });
            const from = data.fromUserId != null ? String(data.fromUserId) : '';
            const authUser = userRef.current;
            const uid =
              authUser?.userId != null ? normUserId(String(authUser.userId)) : '';
            const fromNorm = from ? normUserId(from) : '';
            const isSelf = uid !== '' && fromNorm !== '' && fromNorm === uid;
            if (from && !isSelf) {
              const {
                callerName,
                callMode,
                callScope,
                inviteId: inviteIdFromPayload,
                groupTitle,
              } = parseWsInvitePayload(data.payload, tr('callGeneric'));
              const avatarExtra = {
                ...callerAvatarFromWsSignal(data as Record<string, unknown>),
                ...(groupTitle ? { groupTitle } : {}),
              };
              /**
               * Занята ли линия.
               *
               * <p>Проверять это здесь, а не на экране звонка, обязательно:
               * экран живёт в одном чате, а звонить могут из любого другого —
               * и тогда до него сигнал просто не доходит. Без этой проверки
               * входящий из чужого чата показывал полноэкранный экран поверх
               * живого разговора и играл рингтон в ту же аудиосессию, а
               * звонящий не получал ничего и слушал гудки до таймаута.
               *
               * <p>Занятость в <b>том же</b> чате (второй звонящий, встречный
               * вызов) разбирает сам экран звонка: там есть нога разговора и
               * есть что класть трубкой. Здесь — только чужой чат.
               */
              const busy = decideIncomingInviteWhileBusy(getActiveCall(), {
                chatId: chatIdRaw,
                callInviteId: inviteIdFromPayload ?? null,
                scope: callScope,
              });
              if (busy.action === 'busy' && busy.reason === 'other_chat') {
                voiceCallLog('WS invite while busy: replying call_line_busy', {
                  chatId: chatIdRaw,
                  reason: busy.reason,
                });
                sendSignal(chatIdRaw, {
                  signalType: 'call_line_busy',
                  ...(inviteIdFromPayload ? { callInviteId: inviteIdFromPayload } : {}),
                  targetUserId: from,
                  payload: JSON.stringify({
                    ...(inviteIdFromPayload ? { callInviteId: inviteIdFromPayload } : {}),
                    targetUserId: from,
                  }),
                });
                dispatchSignalToChatListeners(chatIdRaw, rawStr);
                return;
              }
              const route = routeRef.current;
              const onCallSameChat =
                route.name === 'Call' &&
                route.chatId != null &&
                canonChatId(route.chatId) === chatKey;
              /** Локально только что отправили invite (чат / полноэкранный Call) — не показывать входящий UI на web. */
              const suppressWebIncomingInviteUi =
                Platform.OS === 'web' && hasLocalOutgoingCallInvite(chatIdRaw);
              if (!onCallSameChat && !suppressWebIncomingInviteUi) {
                // Только в foreground считаем звонок «уже показанным в приложении» — иначе push
                // ошибочно подавлялся, хотя поверх Home экран звонка не видно.
                // Android: не помечать здесь — см. ветку WS + Telecom ниже (иначе глушим FCM/Notifee
                // до displayIncomingCall и на эмуляторе звонок «пропадает»).
                if (AppState.currentState === 'active' && Platform.OS !== 'android') {
                  markIncomingCallHandledFromWebSocket(chatIdRaw);
                }
                if (Platform.OS === 'web') {
                  // Web has no system call UI — keep the in-app overlay.
                  presentIncomingCallUI({
                    chatId: chatIdRaw,
                    callerName,
                    callMode,
                    callScope,
                    inviteId: inviteIdFromPayload,
                    ...avatarExtra,
                  });
                } else if (Platform.OS === 'ios') {
                  // iOS: do NOT call RNCallKeep.displayIncomingCall from the
                  // WS path. The canonical wakeup for incoming calls on iOS
                  // is the APNs VoIP push, which our native
                  // VrmsgrVoIPDelegate forwards to
                  // `[RNCallKeep reportNewIncomingCall:...]` synchronously
                  // (before JS is even alive on cold launch). The PushKit
                  // payload's UUID and ours often differ — if we *also* call
                  // displayIncomingCall from JS, iOS ends up with two
                  // separate CallKit entries for the same logical call, and
                  // answering one leaves the other ringing. We still need
                  // to stash the pending-call payload so the answer flow
                  // can hydrate it.
                  void (async () => {
                    try {
                      const { storePendingCall } = await import(
                        '../notifications/incomingCallBridge'
                      );
                      const uuid = nativeInvitePendingUuid(
                        inviteIdFromPayload,
                        chatIdRaw,
                        Date.now(),
                      );
                      storePendingCall(uuid, {
                        chatId: chatIdRaw,
                        callerName,
                        callMode,
                        callScope,
                        inviteId: inviteIdFromPayload,
                        ...avatarExtra,
                      });
                    } catch (err) {
                      voiceCallWarn('WS invite (iOS): storePendingCall failed', err);
                    }
                  })();
                } else {
                  // Android: системный Telecom (ConnectionService) на части прошивок молча не
                  // рисует экран стороннего не-self-managed phone account — входящий не виден
                  // вообще. Надёжный путь — тот же in-app оверлей, что и на web. Показываем его
                  // только когда приложение активно (иначе оверлей не виден). Фоновый / убитый
                  // сценарий покрывает FCM-обработчик (полноэкранное уведомление → оверлей).
                  if (AppState.currentState === 'active') {
                    markIncomingCallHandledFromWebSocket(chatIdRaw);
                    voiceCallLog('WS invite (Android): presenting in-app overlay', {
                      chatId: chatIdRaw,
                      callMode,
                    });
                    presentIncomingCallUI({
                      chatId: chatIdRaw,
                      callerName,
                      callMode,
                      callScope,
                      inviteId: inviteIdFromPayload,
                      ...avatarExtra,
                    });
                  }
                }
              }
            }
            dispatchSignalToChatListeners(chatIdRaw, rawStr);
          } else if (data.type === 'SIGNAL' && data.chatId) {
            const chatIdRaw = String(data.chatId);
            const ext = data as { sdp?: string; candidate?: string };
            voiceCallLog('WS SIGNAL in', {
              chatId: chatIdRaw,
              signalType: data.signalType,
              fromUserId: data.fromUserId,
              rawLen: rawStr.length,
              sdpLen: typeof ext.sdp === 'string' ? ext.sdp.length : 0,
              hasCandidate: typeof ext.candidate === 'string' && ext.candidate.length > 0,
            });
            /**
             * Перестал ли звонок быть входящим для нас.
             *
             * <p>Проверять это надо здесь, а не на экране звонка: экран входящего рисует
             * оверлей (или CallKit), и экрана звонка в этот момент ещё нет — человек
             * сидит в списке чатов. Раньше отменить звонящий входящий не мог никто, и
             * `accept` со второго устройства того же аккаунта не значил для телефона
             * ровным счётом ничего: он звонил до самого таймаута.
             */
            const ringing = getRingingIncomingCall(chatIdRaw);
            if (ringing) {
              const decision = decideIncomingRingCancel(ringing, {
                chatId: chatIdRaw,
                signalType: String(data.signalType ?? '').toLowerCase(),
                fromUserId: data.fromUserId != null ? String(data.fromUserId) : null,
                legFromMessage: extractCallInviteIdFromSignal(data as Record<string, unknown>),
                selfUserId:
                  userRef.current?.userId != null ? String(userRef.current.userId) : null,
              });
              if (decision.action === 'cancel') {
                void cancelRingingIncomingCall(chatIdRaw, decision.reason);
              }
            }
            dispatchSignalToChatListeners(chatIdRaw, rawStr);
          }
        } catch (err) {
          voiceCallWarn('WS onmessage parse error', err);
        }
      };
    };

    void connect();

    return () => {
      isActive = false;
      bumpConnectGeneration('realtime_provider_effect_cleanup');
      logCallLifecycleEnd('ws_effect_cleanup_start', {
        extra: { willCloseSocket: wsRef.current != null },
      });
      // Preserve signalBacklogRef / signalQueueRef across this effect's teardown so a
      // token refresh or dependency churn does not wipe in-flight call signaling.
      // Logout clears both in the `if (!accessToken)` branch above.
      if (wsPingTimer) {
        clearInterval(wsPingTimer);
        wsPingTimer = null;
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [accessToken, applyAccessToken, dispatchSignalToChatListeners, invalidateSession, user?.userId, routeRef, sendSignal]);

  const value = useMemo(
    () => ({
      wsConnected,
      subscribeMessages,
      subscribeMessageDeletes,
      subscribeMessageReads,
      subscribeIncomingMessages,
      subscribePresence,
      subscribeSignals,
      subscribeChannelPostComments,
      subscribeChatDeleted,
      subscribeDirectMessagesPurged,
      subscribeCallSession,
      subscribeChatListChanged,
      subscribeContactsChanged,
      subscribeVoiceTranscripts,
      sendSignal,
      refreshSubscribeAll,
    }),
    [
      wsConnected,
      subscribeMessages,
      subscribeMessageDeletes,
      subscribeMessageReads,
      subscribeIncomingMessages,
      subscribePresence,
      subscribeSignals,
      subscribeChannelPostComments,
      subscribeChatDeleted,
      subscribeDirectMessagesPurged,
      subscribeCallSession,
      subscribeChatListChanged,
      subscribeContactsChanged,
      subscribeVoiceTranscripts,
      sendSignal,
      refreshSubscribeAll,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useGlobalRealtime(): GlobalRealtimeValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useGlobalRealtime outside provider');
  return v;
}
